// src/pages/Register.js
import { useState } from "react";
import {
  Box, TextField, Button, Typography, Alert,
  InputAdornment, IconButton, CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, Link as RouterLink, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../api/axios";
import { useAuth } from "../auth/useAuth";

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [username,   setUsername]   = useState("");
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [showPwd,    setShowPwd]    = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [errMsg,     setErrMsg]     = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleRegister = async (e) => {
    e?.preventDefault?.();
    setErrMsg(""); setSuccessMsg(""); setLoading(true);
    try {
      const res = await API.post("/auth/register", { username, email, password });
      setSuccessMsg(res?.data?.message || t("auth.register_success"));
      navigate("/login", { replace: true });
    } catch (err) {
      setErrMsg((((d) => typeof d === 'string' ? d : d?.message || d?.detail || JSON.stringify(d))(err?.response?.data?.detail)) || err?.message || t("auth.register_error"));
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: "#f8fafc",
      background: "radial-gradient(ellipse 80% 60% at 50% -10%, #eef2ff 0%, #f8fafc 60%)",
    }}>
      <Box sx={{ width: "100%", maxWidth: 420, px: 2 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2, bgcolor: "#6366f1",
            mx: "auto", mb: 2, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ color: "white", fontWeight: 800, fontSize: 20 }}>N</Typography>
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>NextHop Academy</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("auth.register_subtitle")}
          </Typography>
        </Box>

        <Box sx={{
          bgcolor: "white", borderRadius: 3, border: "1px solid", borderColor: "#e2e8f0",
          p: 3.5, boxShadow: "0 4px 24px rgba(0,0,0,.06)",
        }} component="form" onSubmit={handleRegister}>

          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5 }}>
            {t("auth.title_register")}
          </Typography>

          {errMsg     && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }}>{errMsg}</Alert>}
          {successMsg && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{successMsg}</Alert>}

          <TextField label={t("auth.username")} fullWidth size="small" margin="normal"
            value={username} onChange={e => setUsername(e.target.value)} autoComplete="username"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField label={t("auth.email")} type="email" fullWidth size="small" margin="normal"
            value={email} onChange={e => setEmail(e.target.value)} autoComplete="email"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField
            label={t("auth.password")} type={showPwd ? "text" : "password"}
            fullWidth size="small" margin="normal"
            value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password"
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPwd(v => !v)}>
                  {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )}}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <Button type="submit" variant="contained" fullWidth disabled={loading}
            sx={{ mt: 2, py: 1.2, borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, fontWeight: 600 }}>
            {loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : t("auth.register")}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, textAlign: "center" }}>
            {t("auth.already_account")}{" "}
            <RouterLink to="/login" style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>
              {t("auth.go_login")}
            </RouterLink>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
